import type { UseFetchFromPublicWabunkaApiArgs } from '@/composables/useFetchFromPublicWabunkaApi';
import { useAsyncFetchFromPublicWabunkaApi } from '@/wabunka-src/common/composables/query/useAsyncFetchFromPublicWabunkaApi';
import { buildFetchKey } from '@/wabunka-src/common/utils/buildFetchKey';

/**
 * パスとクエリを元に、FetchのKeyを生成する（キャッシュキーの安定性を担保するため単体テストあり）
 * @param path
 * @param query
 * @returns string
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const generateFetchKey = (path: string, query: Record<string, any> | undefined): string => {
  // 先頭の'/'を取り除き、残りの'/'を'-'に置き換える
  const formattedPath = path.replace(/^\//, '').replace(/\//g, '-');

  // クエリが空の場合はパスのみを返却
  if (!query || Object.keys(query).length === 0) return formattedPath;

  // クエリをプロパティ名でソートして文字列化
  const queryString = Object.keys(query)
    .sort()
    .reduce((acc, key) => {
      return acc + '&' + key + '=' + query[key];
    }, '');

  return `${formattedPath}?${queryString}`;
};

/**
 * generateFetchKey にリクエスト内容を足した key を返す。
 *
 * generateFetchKey は path と query しか見ないため、同じ path への body 違いの POST が同一 key になり、
 * 2 回目が 1 回目の応答をそのまま返す（ADR-094）。method も body も無い GET では戻り値が変わらない。
 */
export const buildUniqueFetchKey = (
  path: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  query: Record<string, any> | undefined,
  request?: { method?: string; body?: object },
): string => buildFetchKey(generateFetchKey(path, query), undefined, undefined, request);

/**
 * Public Wabunka API からデータを取得する際に、FetchのKeyをユニークにするためのラッパー Composable （※ページ層では直接使用せず、ドメイン知識を有した Composable を介して利用してください）
 * Keyをユニークにすることで、取得したデータがリクエスト間で重複排除されず、データを個別に扱うことができる
 *
 * @param UseFetchAsyncFromCalchezApiArgs
 * @return useFetchAsyncFromCalchezApiと同じ戻り値
 */
export const useAsyncFetchWithUniqueKeyFromPublicWabunkaApi = async <T>({ path, query, method, body, options, converter, throwOnError }: UseFetchFromPublicWabunkaApiArgs<T>) => {
  const fetchKey = buildUniqueFetchKey(path.value, query?.value, { method, body: body?.value });
  // method / body を落とすと、POST を渡した呼び出しが無言で GET になり本文も飛ばない
  const { data, status, refresh } = await useAsyncFetchFromPublicWabunkaApi({ path, query, method, body, options, converter, fetchKey, throwOnError });

  return { data, status, refresh };
};
